/**
 * iter740 — "Install as App" banner for the landing page.
 *
 * One component, two audiences:
 *   • Android Chrome → triggers the native `beforeinstallprompt` flow,
 *     so a single tap installs the PWA like a Play-Store app (no APK,
 *     no signing, no Emergent infra needed).
 *   • iOS Safari → Safari blocks programmatic install, so we render
 *     a concise 3-step "Share → Add to Home Screen" instruction card.
 *   • Desktop browsers → same Chrome prompt path; falls back to a
 *     neutral "Open on your phone" message.
 *
 * Also surfaces a discreet "Android APK" download link in case the
 * user prefers to sideload (e.g. firewalled enterprise networks where
 * Play Store / PWA install is blocked). The APK itself is built on
 * Emergent's build pipeline — this is just the front-end CTA.
 */
import { useEffect, useState } from 'react';
import { Smartphone, Apple, Download, X, Share2, Plus } from 'lucide-react';

const APK_DOWNLOAD_URL = '/api/apk/download';

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export default function InstallAppBanner() {
  const [platform, setPlatform] = useState('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    // Already installed PWA / TWA?
    if (typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }
    if (typeof localStorage !== 'undefined' && localStorage.getItem('smarto_install_dismissed') === '1') {
      setDismissed(true);
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeinstallprompt', handler);
      window.addEventListener('appinstalled', () => setInstalled(true));
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstall = async () => {
    if (platform === 'ios') {
      setShowIosSheet(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
    } else {
      // Browser doesn't support beforeinstallprompt — surface the iOS sheet style
      // as a universal "how to install" guide instead.
      setShowIosSheet(true);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem('smarto_install_dismissed', '1'); } catch { /* noop */ }
  };

  if (installed || dismissed) return null;

  return (
    <>
      <div
        className="relative max-w-5xl mx-auto px-4 sm:px-6 mb-6"
        data-testid="install-app-banner"
      >
        <div className="rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-5 sm:p-6 shadow-2xl shadow-slate-900/20 relative overflow-hidden">
          {/* Subtle decorative ring */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#0055FF]/10 blur-3xl" aria-hidden />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl" aria-hidden />

          <button
            onClick={dismiss}
            className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors"
            aria-label="Dismiss"
            data-testid="install-banner-dismiss"
          >
            <X size={16} />
          </button>

          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#4D8DFF] to-[#0040CC] flex items-center justify-center shadow-lg shadow-[#0055FF]/30">
              <Smartphone size={26} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-[#7FB0FF] font-semibold mb-1">Now available</div>
              <h3 className="text-lg sm:text-xl font-bold">Install Smarto on your phone or tablet</h3>
              <p className="text-sm text-white/70 mt-1">
                Lightning-fast access, full-screen app feel, works offline for menu browsing.
                {platform === 'ios' && ' Tap Install for Safari instructions.'}
                {platform === 'android' && ' Tap Install — single tap install on Android.'}
                {platform === 'desktop' && ' Open this page on your phone to install.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#4D8DFF] text-white px-4 py-2.5 rounded-full font-semibold text-sm shadow-lg shadow-[#0055FF]/30 transition-all hover:scale-105 active:scale-95"
                data-testid="install-banner-cta"
              >
                {platform === 'ios' ? <Apple size={16} /> : <Smartphone size={16} />}
                Install
              </button>
              <a
                href={APK_DOWNLOAD_URL}
                download
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs underline-offset-2 hover:underline transition-colors"
                data-testid="install-banner-apk"
                title="Direct Android APK download (sideload). Use only if Play Store / PWA install is blocked on your network."
              >
                <Download size={12} /> Android APK
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* iOS install instruction sheet */}
      {showIosSheet && (
        <div
          className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosSheet(false)}
          data-testid="ios-install-sheet"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-bold text-stone-900 inline-flex items-center gap-2">
                <Apple size={20} /> Install on iPhone / iPad
              </h4>
              <button onClick={() => setShowIosSheet(false)} className="text-stone-400 hover:text-stone-700">
                <X size={18} />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-stone-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#DCE8FF] text-[#0040CC] font-bold text-xs flex items-center justify-center">1</span>
                <span>Open <strong>smartoerp.com</strong> in <strong>Safari</strong> (not Chrome — iOS PWA install only works in Safari).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#DCE8FF] text-[#0040CC] font-bold text-xs flex items-center justify-center">2</span>
                <span className="inline-flex items-center gap-1 flex-wrap">Tap the <Share2 size={14} className="inline" /> <strong>Share</strong> button at the bottom of the screen.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#DCE8FF] text-[#0040CC] font-bold text-xs flex items-center justify-center">3</span>
                <span className="inline-flex items-center gap-1 flex-wrap">Scroll down and tap <Plus size={14} className="inline" /> <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center">✓</span>
                <span>The Smarto icon appears on your Home Screen. Opens full-screen, no Safari address bar.</span>
              </li>
            </ol>
            <p className="text-xs text-stone-500 mt-4 leading-relaxed">
              Auto-updates instantly whenever we ship new features. No App Store needed.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
